import { ActionConstants } from '../../constants/action-constants';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { Constants } from '../../shared/shared-constants';
import { sanitizeAndFormatString } from '../../shared/utils/string.util';
import { A365Named } from '../base/a365Named';
import { Collection, IAttribute, IViolation } from '../base/annotation';
import { Metamodel } from '../base/model';
import { ParameterValue } from '../base/parameter-value';
import { LinkedService } from '../linkedservice/linkedservice';
import { ModelConstants } from '../model-constants';
import { ModelMarker } from '../services/state';
import { LinkTable } from './linktable';

export interface ILinkTopicStatus {
    name: string;
    id: string;
    status: 'Initial' | 'Starting' | 'Running' | 'Stopping' | 'Stopped';
    error?: {
        errorCode?: string;
        message?: string;
        details?: string[];
    };
    startTime?: string;
    endTime?: string;
}

@Metamodel.model({
    name: ModelConstants.linkConnection,
    display: [shellStringResources.LinkConnection],
    reusable: true,
    constraints: {
        name: {
            required: true,
            patterns: [
                {
                    message: sanitizeAndFormatString(shellStringResources.LinkTopicNameValidationError, shellStringResources.LinkConnection),
                    regex: Constants.blobContainerNameValidation
                }
            ],
            uniqueModelName: true
        }
    },
    accessPublishConstraint: {
        writeResource: {
            action: ActionConstants.linkConnectionsWrite
        },
        deleteResource: {
            action: ActionConstants.linkConnectionsDelete
        }
    }
})
export class LinkTopic extends A365Named {
    public static readonly TARGET_LINKED_SERVICE_PARAMETERS: IAttribute;
    public readonly useShellFolder = true;
    public readonly liveOnly = true;

    @Metamodel.attribute({
        reference: true,
        type: LinkedService.MODEL,
        constraint: {
            required: true
        }
    })
    public sourceLinkedService: LinkedService;

    @Metamodel.attribute({
        reference: true,
        type: LinkedService.MODEL,
        constraint: {
            required: true
        }
    })
    public targetLinkedService: LinkedService; // target linked service should always be the default sql server linked service of the workspace

    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            required: false
        },
        association: 'TARGET_LINKED_SERVICE_PARAMETERS'
    })
    public targetLinkedServiceParameters: ParameterValue[] = [];

    @Metamodel.attribute({
        reference: true,
        type: LinkedService.MODEL
    })
    public landingZoneLinkedService?: LinkedService;

    @Metamodel.attribute({
        reference: true,
        type: LinkedService.MODEL
    })
    public lakehouseLinkedService?: LinkedService;

    public landingZoneToken?: string;
    public landingZoneFileSystem?: string;
    public landingZoneFolder?: string;

    public resourceId: string;
    public principalId: string;

    public targetDatabaseName: string;
    public targetSqlPoolName: string;
    public crossTableTransaction: boolean;
    public dropExistingTargetTableOnStart: boolean;
    public actionOnExistingTargetTable: 'FailOnNonEmptyTable' | 'DropAndRecreateTable' | 'MergeWithExistingData';

    public computeCoreCount: number = 16;
    public computeType: string;
    public dataProcessIntervalInMinutes: number;
    public vacuum?: number;
    public optimizedWrite: boolean = false;
    public autoCompact: boolean = false;

    public topicStatus: 'Initial' | 'Starting' | 'Running' | 'Stopping' | 'Stopped' | 'Paused' | 'Pausing' | 'Resuming';
    public lastCommittedLsn: string;
    public error: {};
    public executionStartTime: string;
    public executionEndTime: string;

    @Metamodel.attribute({
        constraint: {
            validator: (owner: LinkTopic, value: LinkTable[]): IViolation[] => {
                if (value && value.length) {
                    const sourceTableNameSet = new Set();
                    const targetTableNameSet = new Set();
                    for (const table of value) {
                        if (ModelMarker.isDeleted(table)) {
                            continue;
                        }
                        const sourceTableName = `${table.sourceSchemaName}.${table.sourceTableName}`;
                        if (sourceTableNameSet.has(sourceTableName)) {
                            return [
                                {
                                    context: owner,
                                    message: () => sanitizeAndFormatString(
                                        shellStringResources.LinkTopicSourceTargetTablesUniqueValidationError,
                                        shellStringResources.Source,
                                        sourceTableName,
                                        owner.name
                                    )
                                }
                            ];
                        }
                        sourceTableNameSet.add(sourceTableName);
                        const targetTableName = `${table.targetSchemaName}.${table.targetTableName}`;
                        if (targetTableNameSet.has(targetTableName)) {
                            return [
                                {
                                    context: owner,
                                    message: () => sanitizeAndFormatString(
                                        shellStringResources.LinkTopicSourceTargetTablesUniqueValidationError,
                                        shellStringResources.Target, targetTableName, owner.name
                                    )
                                }
                            ];
                        }
                        targetTableNameSet.add(targetTableName);
                    }
                }
                return undefined;
            }
        }
    })
    public linkTables: LinkTable[] = [];

    /* This flag is used to determine whether a link topic itself is pending publish.
       Sometimes, a link topic is dirty because its link tables are changed but not itself is changed, then isPendingPublish shoulde be false;
       The value of the flag is determined right before publishing.
    */
    public isSelfPendingPublish?: boolean = false;

    // This flag is used to determine whether the link tables of a link topic are already fetched from backend or not.
    public linkTablesAreFetched = false;

    constructor(name?: string, description?: string) {
        super(name, description, null);
        this.resourceType = ModelConstants.linkConnection + 's';
    }

}
